import { getAxiosClient } from '~src/api';
import { changeImageSrc } from '~src/utils';
import { onErrorShowMessage } from '~src/errors';
import { getState } from '~src/state';
import { triggerStateChangedRequest } from '~core';
import { trackEvent } from '~src/tracking';
import { FEATURES, isFeatureEnabled } from '~src/features';
import { prepareImageForEditing } from '~core/image';
import { getBase64ImageByUrl } from '~src/utils';
import { b64toBlob } from '~core';
import {
  startAiLoader,
  stopAiLoader,
  updateAiLoaderForSaving,
  showAiSuccessMessage,
  sleep,
  AI_FEATURE_LOADING_MESSAGES,
} from '~core/aiLoader';
import { isPlaceholderEditing } from '~core/magicFrame';
import { checkAiCreditsBeforeUse } from '~core/aiCredits';

export default async function handleSmile() {
  const allowUseSmile = await isFeatureEnabled(FEATURES.AI_USE_SMILE, false);
  if (!allowUseSmile) {
    alert('Questa funzione non è disponibile nel tuo piano di abbonamento');
    return;
  }

  // Check credits before starting the operation
  const hasCredits = await checkAiCreditsBeforeUse({
    featureName: 'Smile',
    requiredCredits: 1,
  });

  if (!hasCredits) {
    return;
  }

  const startTime = performance.now();
  let selection = window.canvas.getActiveObject();
  if (selection.type === 'MagicFrame') {
    selection = selection.editingImg;
  }

  if (isPlaceholderEditing(selection)) {
    return;
  }

  const imageUrl = await prepareImageForEditing(selection, false);
  if (!imageUrl) {
    console.error('No image URL found in current selection', selection);
    return;
  }

  const loaderInstance = startAiLoader(
    AI_FEATURE_LOADING_MESSAGES.SMILE,
    '✨ Aggiungi Sorriso'
  );

  const data = {
    image_url: imageUrl,
    smile: 1,
  };

  let queueResponse;
  let response;

  const maxAttempts = 100;
  const httpClient = await getAxiosClient();
  const httpClientWithRetries = await getAxiosClient(true, maxAttempts);

  try {
    queueResponse = await httpClient.post(
      `${process.env.GIOTTO_API_URL}/expression`,
      data
    );
  } catch (err) {
    console.error(err);
    stopAiLoader();
    return onErrorShowMessage();
  }

  await sleep(1000);

  try {
    response = await httpClientWithRetries.get(
      `${process.env.GIOTTO_API_URL}/expression?requestId=${queueResponse.data.requestId}`,
      data
    );
  } catch (err) {
    console.error(err);
    stopAiLoader();
    return onErrorShowMessage();
  }

  // Update loader to show saving step
  updateAiLoaderForSaving(loaderInstance);

  const imageBase64 = await getBase64ImageByUrl(response.data.imageUrl);
  const blob = b64toBlob(imageBase64, 'image/jpeg');
  const attachmentsUploader = getState('uploader.allegati');

  attachmentsUploader.onComplete = attachmentsUploader.addFile({
    type: blob.type,
    data: blob,
    meta: {
      customAttachmentProps: {
        smileRawImage: imageUrl,
        isSmile: true,
      },
    },
  });

  const responseUpload = await attachmentsUploader.upload();

  if (!responseUpload.successful || responseUpload.successful.length === 0) {
    stopAiLoader();
    return onErrorShowMessage();
  }

  attachmentsUploader.cancelAll();

  const newImageUrl = `${process.env.EDGE_RESIZER}${
    new URL(responseUpload.successful[0].uploadURL).pathname
  }`;

  let magicFrame = selection._magicFrame;
  if (selection.type === 'MagicFrame') {
    magicFrame = selection;
  }

  if (magicFrame) {
    await magicFrame.updatePerson(newImageUrl);
    magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
    magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
  } else {
    const currentProps = {
      scaleX: selection.scaleX,
      scaleY: selection.scaleY,
      top: selection.top,
      left: selection.left,
    };

    await changeImageSrc(selection, newImageUrl);

    selection.set({
      ...currentProps,
    });
  }
  window.canvas.renderAll();

  triggerStateChangedRequest();

  stopAiLoader();

  // Show success message
  showAiSuccessMessage(
    'Sorriso Aggiunto',
    'Il sorriso è stato applicato con successo!'
  );

  trackEvent('AI Smile', {
    success: true,
    time_taken: parseInt(performance.now() - startTime, 10),
  });
}
